import { SyntheticEvent, useCallback, useMemo, useState } from "react";
import { Grid, Tab, Tabs } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import RoundedContentBox from "@/components/general/roundedContentBox";
import ImageViewer from "@/components/general/imageViewer";
import LmsCourseOverviewHeader from "@/components/lms/lmsCourseOverviewHeader";
import LmsBackButton from "@/components/lms/lmsBackButton";
import LmsEnrolmentCreateModal from "@/components/lms/lmsEnrolmentCreateModal";
import LmsEnrolmentsTable from "@/components/lms/lmsEnrolmentsTable";
import LmsStatusOverviewCard from "@/components/lms/lmsStatusOverviewCard";
import GenericNotification from "@/components/notification/genericNotification";
import { RootState } from "@/components/state/store";
import { getImageUrl } from "@/helper/imageUrlHandler";
import { getLmsCourseFeaturedImage } from "@/components/lms/lms.utils";
import LmsCourseOverviewReadOnly from "@/routes/lms/lmsCourseOverviewReadOnly";
import styles from "@/routes/lms/lmsCourseOverview.module.css";

type OverviewTab = "overview" | "statistics" | "enrolments";

export default function LmsManagerCourseOverview() {
  const { t } = useTranslation();
  const subdomain = window.location.hostname.split(".")[0];
  const { courseId } = useParams<{ courseId: string }>();
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const [activeTab, setActiveTab] = useState<OverviewTab>("overview");
  const [course, setCourse] = useState<any>(null);
  const [isEnrolmentModalOpen, setIsEnrolmentModalOpen] = useState(false);
  const [enrolmentRefreshKey, setEnrolmentRefreshKey] = useState(0);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  const handleTabChange = (_event: SyntheticEvent, newValue: OverviewTab) => {
    setActiveTab(newValue);
  };

  const handleCourseLoaded = useCallback((loadedCourse: any) => {
    setCourse(loadedCourse);
  }, []);

  const handleEnrolmentTableRefreshed = useCallback(() => {
    setEnrolmentRefreshKey((currentValue) => currentValue + 1);
  }, []);

  const handleEnrolmentCreated = () => {
    setIsEnrolmentModalOpen(false);
    setNotificationMessage(t("users_enrolled_successfully"));
    setEnrolmentRefreshKey((currentValue) => currentValue + 1);
  };

  const featuredImage = getLmsCourseFeaturedImage(course);

  const featuredImageUrl = featuredImage?.viewLink
    ? getImageUrl(
        featuredImage.viewLink,
        selectedChildCompany.subjectId
          ? String(selectedChildCompany.subjectId)
          : undefined
      )
    : null;

  const featuredImageViewerImages = useMemo(
    () =>
      featuredImage?.viewLink && courseId
        ? [
            {
              id: featuredImage.id || courseId,
              name: featuredImage.displayName,
              view_link: featuredImage.viewLink,
              download_link: featuredImage.viewLink,
              extension: featuredImage.displayName.split(".").pop() || "png",
            },
          ]
        : [],
    [courseId, featuredImage]
  );

  return (
    <>
      <Grid container className={styles.mainGrid}>
        <Grid item xs={12}>
          <div className={styles.courseOverviewNav}>
            <LmsBackButton />
          </div>
          <RoundedContentBox>
            <LmsCourseOverviewHeader
              featuredImageUrl={featuredImageUrl}
              imageAlt={
                featuredImage?.displayName ||
                course?.title ||
                course?.name ||
                t("course_featured_image")
              }
              title={course?.title ?? course?.name ?? t("courses")}
              onViewImage={() => setImageViewerOpen(true)}
              canViewImage={Boolean(featuredImage?.viewLink)}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className={styles.tabsContainer}
                variant="scrollable"
                scrollButtons={false}
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#8c1ec8",
                  },
                  "& .MuiTab-root": {
                    minWidth: "auto",
                    flexShrink: 0,
                    fontSize: {
                      xs: "0.75rem",
                      sm: "0.8125rem",
                      md: "0.875rem",
                    },
                    padding: { xs: "0.75rem 0.625rem", sm: "0.75rem 1rem" },
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "#8c1ec8",
                  },
                }}
              >
                <Tab
                  value="overview"
                  label={t("overview")}
                  data-testid="lms-manager-course-overview-tab"
                />
                <Tab
                  value="statistics"
                  label={t("statistics")}
                  data-testid="lms-manager-course-statistics-tab"
                />
                <Tab
                  value="enrolments"
                  data-testid="lms-manager-course-enrolments-tab"
                  label={t("enrolments")}
                />
              </Tabs>
            </LmsCourseOverviewHeader>
          </RoundedContentBox>
        </Grid>

        <Grid
          item
          xs={12}
          className={
            activeTab !== "overview" ? styles.tabPanelHidden : undefined
          }
          aria-hidden={activeTab !== "overview"}
        >
          <LmsCourseOverviewReadOnly
            requiredMode="manager"
            subdomain="manage"
            renderOnlyOverview
            onCourseLoaded={handleCourseLoaded}
          />
        </Grid>

        {activeTab === "statistics" && courseId && (
          <Grid item xs={12}>
            <div className={styles.statisticsTabContentSection}>
              <LmsStatusOverviewCard
                mode="manager"
                courseId={courseId}
                companySubjectId={
                  selectedChildCompany.subjectId
                    ? String(selectedChildCompany.subjectId)
                    : undefined
                }
                dualDisplayMode={true}
              />
            </div>
          </Grid>
        )}

        {activeTab === "enrolments" && (
          <Grid item xs={12}>
            <LmsEnrolmentsTable
              courseId={courseId}
              mode="manager"
              includeCourseColumn={false}
              onCreate={() => setIsEnrolmentModalOpen(true)}
              refreshKey={enrolmentRefreshKey}
              onRefreshed={handleEnrolmentTableRefreshed}
              showViewCourseButton={false}
            />
          </Grid>
        )}
      </Grid>

      <LmsEnrolmentCreateModal
        open={isEnrolmentModalOpen}
        courseId={courseId}
        mode="manager"
        onClose={() => setIsEnrolmentModalOpen(false)}
        onCompleted={handleEnrolmentCreated}
        enableCourseSelection={false}
      />

      {featuredImageViewerImages.length > 0 && (
        <ImageViewer
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          images={featuredImageViewerImages}
          initialImageIndex={0}
          doNotShowFileName={subdomain !== "admin"}
        />
      )}

      {notificationMessage && (
        <GenericNotification
          message={notificationMessage}
          variant="success"
          handleCloseSnackbar={() => setNotificationMessage("")}
        />
      )}
    </>
  );
}
